import { Box, Flex, Heading, Text, Button, Image } from '@everlywell/ui-kit';
import { Link } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import React from 'react';

import { ResourceType } from './ResourcesFilterBar';
import { ContentfulBlogPost } from './ResourcesGrid';

interface ResourceCardProps {
  resource: ContentfulBlogPost;
  shadow?: boolean;
}

const getActionText = (resource: ContentfulBlogPost): string => {
  if (resource.ctaText) return resource.ctaText;

  switch (resource.contentType) {
    case '1 Pager':
      return 'Download';
    case 'Whitepaper':
      return 'Download';
    case 'Case Study':
      return 'Read More';
    default:
      return 'Read More';
  }
};

const getTypeBadgeInfo = (type?: ResourceType | string) => {
  switch (type) {
    case '1 Pager':
      return {
        src: 'https://images.ctfassets.net/6huuyzvsw0nv/6XxH8RCXl8EdgTAu9Frumx/0962b108f3b454fe23836bce2b15212b/1-pager.svg',
        alt: 'One Pager',
      };
    case 'Whitepaper':
      return {
        src: 'https://images.ctfassets.net/6huuyzvsw0nv/37fdi3TwtwLRI0pa2eYhq3/6ccf82df3d0a1d46914df64f3b302fb5/whitepaper.svg',
        alt: 'Whitepaper',
      };
    case 'Case Study':
      return {
        src: 'https://images.ctfassets.net/6huuyzvsw0nv/2QuofExJxqq5Rq61TdVNWN/63a5320d6a7a940f94c50ba4d4355560/case-study.svg',
        alt: 'Case Study',
      };
    default:
      return null;
  }
};

export const ResourceCard: React.FC<ResourceCardProps> = ({
  resource,
  shadow = false,
}) => {
  const image = getImage(resource.featuredImage);
  const typeBadge = getTypeBadgeInfo(resource.contentType);

  return (
    <Box
      bg="#F9F9F6"
      borderRadius="md"
      overflow="hidden"
      height="100%"
      display="flex"
      flexDirection="column"
      position="relative"
      role="article"
      aria-label={`Resource: ${resource.title}`}
      data-testid="resource-card"
      sx={{
        willChange: 'transform, opacity, box-shadow',
        boxShadow: shadow ? '0px 4px 35px 0px #00000014' : 'none',
      }}
    >
      {/* Resource type badge with icon */}
      {typeBadge && (
        <Image
          src={typeBadge.src}
          alt={typeBadge.alt}
          width={83}
          height={28}
          position="absolute"
          top={4}
          left={4}
          zIndex={1}
        />
      )}

      {/* Image */}
      <Link
        to={`/resources/${resource.slug}`}
        aria-label={`View details for ${resource.title}`}
      >
        <Box position="relative" borderRadius="lg">
          {image ? (
            <GatsbyImage
              image={image}
              alt={resource.featuredImage.description || resource.title}
              style={{
                height: '220px',
                width: '100%',
                objectFit: 'cover',
                transition: 'transform 0.3s ease-in-out',
              }}
            />
          ) : (
            <Box
              height="250px"
              bg="gray.100"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
            >
              <Text color="gray.500" textAlign="center" px={4}>
                {resource.featuredImage?.title || 'Image not available'}
              </Text>
            </Box>
          )}
        </Box>
      </Link>

      {/* Content */}
      <Box p={4} display="flex" flexDirection="column" flex="1">
        <Box mb={6} flex="1" mt={[3, 3, 0]}>
          <Heading
            as="h3"
            fontSize={['16px', '20px']}
            lineHeight="1.2"
            fontWeight="medium"
            textAlign={['center', 'center', 'left']}
          >
            {resource.title}
          </Heading>
        </Box>

        {/* Action button */}
        <Link
          to={`/resources/${resource.slug}`}
          aria-label={`${getActionText(resource)} ${resource.title}`}
        >
          <Flex
            justifyContent="space-between"
            alignItems="center"
            mt={5}
            flexDirection="row"
          >
            <Text
              fontSize="12px"
              fontWeight="medium"
              color="#949494"
              textAlign={['center', 'center', 'left']}
            >
              {[resource.audience, resource.focus].filter(Boolean).join(', ')}
            </Text>
            <Button
              backgroundColor="white"
              color="#000"
              size="lg"
              fontSize="16px"
              fontWeight="medium"
              px={8}
              py={1}
              _hover={{
                backgroundColor: 'white',
              }}
              transition="all 0.2s ease-in-out"
            >
              {getActionText(resource)}
            </Button>
          </Flex>
        </Link>
      </Box>
    </Box>
  );
};

export default ResourceCard;
